import React, { useState, useEffect } from 'react'
import { 
  Card, 
  CardBody, 
  Avatar, 
  Button, 
  Tabs, 
  Tab, 
  Chip
} from '@heroui/react'
import RenderizadorDiseño from './DiseñosEnlaces'

const VistaPerfilPublico = ({ 
  usuario, 
  esPerfilPropio, 
  estiloMiniatura, 
  estilosPerfil, 
  tipografia,
  abrirModalNuevoEnlace,
  RenderizarCuentaBancaria,
  renderIcon 
}) => {
  // Estado para el tab seleccionado
  const [tabSeleccionado, setTabSeleccionado] = useState('general')
  const [enlacesFiltrados, setEnlacesFiltrados] = useState([])

  // Efecto para filtrar enlaces cuando cambia el tab seleccionado
  useEffect(() => {
    if (usuario.enlaces && usuario.tabs && usuario.tabs.length > 0) {
      const enlacesDelTab = usuario.enlaces.filter(enlace => 
        enlace.tab === tabSeleccionado || (!enlace.tab && tabSeleccionado === 'general')
      )
      setEnlacesFiltrados(enlacesDelTab)
    } else if (usuario.enlaces) {
      // Si no hay tabs, mostrar todos los enlaces
      setEnlacesFiltrados(usuario.enlaces)
    } else {
      setEnlacesFiltrados([])
    }
  }, [tabSeleccionado, usuario.enlaces, usuario.tabs])

  // Clases para el estilo miniatura
  const clasesMiniatura =  {
    container: "min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-4 sm:py-8",
    card: "shadow-lg",
    cardBody: "p-4 sm:p-8",
    avatar: "w-24 h-24 sm:w-32 sm:h-32 border-4 border-blue-500",
    title: "text-2xl sm:text-3xl font-bold text-gray-800 mb-2",
    username: "text-blue-600 text-base sm:text-lg mb-2",
    bio: "text-gray-600 mb-3 sm:mb-4 max-w-2xl",
    info: "text-gray-600",
    linksTitle: "text-xl sm:text-2xl font-bold text-gray-800 mb-1 sm:mb-6 text-center",
    linkCard: "p-3 sm:p-4 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all duration-200 group",
    linkIcon: "w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors",
    linkIconText: "text-lg sm:text-xl",
    linkTitle: "font-semibold text-gray-800 group-hover:text-blue-600 transition-colors text-sm",
    linkDescription: "text-gray-600 text-sm mt-1",
    arrow: "text-gray-400 group-hover:text-blue-500 transition-colors"
  }

  return (
    <div className={estiloMiniatura ? "max-w-xs mx-auto" : "max-w-4xl mx-auto px-0.5 sm:px-4"}>
      {/* Header del perfil */}
      <Card 
        className={`mb-4 ${clasesMiniatura.card}`}
        style={{
          backgroundColor: estilosPerfil.fondoSecundario,
          borderColor: estilosPerfil.borde
        }}
      >
        <CardBody 
          className={clasesMiniatura.cardBody}
          style={{ color: estilosPerfil.texto }}
        >
          <div className={`flex ${estiloMiniatura ? 'flex-col' : 'flex-col md:flex-row'} items-center gap-${estiloMiniatura ? '3' : '6'}`}>
            {/* Avatar */}
            <div className="flex-shrink-0 relative">
              <Avatar
                src={usuario.fotoPerfil}
                size={estiloMiniatura ? "lg" : "2xl"}
                className={clasesMiniatura.avatar}
                name={usuario.nombre}
              />
            </div>
            
            {/* Información del usuario */}
            <div className="flex-1 text-center md:text-left">
              {/* Nombre */}
              <h1 className={clasesMiniatura.title}>
                {usuario.nombre}
              </h1>
              
              <p className={clasesMiniatura.username}>
                @{usuario.nombreUsuario}
              </p>
              
              {/* Biografía */}
              {usuario.bio && (
                <p className={clasesMiniatura.bio}>
                  {usuario.bio}
                </p>
              )}
              
              {/* Ubicación */}
              {usuario.ubicacion && (
                <div className={`flex items-center justify-center md:justify-start gap-2 ${estiloMiniatura ? 'mb-1' : 'mb-2'}`}>
                  <span className="text-gray-500">📍</span>
                  <span className={clasesMiniatura.info}>{usuario.ubicacion}</span>
                </div>
              )}
              
              {/* Sitio Web */}
              {usuario.sitioWeb && (
                <div className="flex items-center justify-center md:justify-start gap-2">
                  <span className="text-gray-500">🌐</span>
                  <a 
                    href={usuario.sitioWeb} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`text-blue-600 hover:text-blue-800 hover:underline ${clasesMiniatura.info}`}
                  >
                    {estiloMiniatura ? 'Sitio web' : usuario.sitioWeb}
                  </a>
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Sección de enlaces */}
      {usuario.enlaces && usuario.enlaces.length > 0 && (
        <div 
          className={" rounded-2xl shadow-lg"}
          style={{
            backgroundColor: estilosPerfil.fondoSecundario,
            borderColor: estilosPerfil.borde
          }}
        >
          <div 
            className={""}
            style={{ color: estilosPerfil.texto }}
          >
            <div className="flex items-center justify-between pt-4 pl-4 rounded-2xl">
              <h2 className={clasesMiniatura.linksTitle}>
                Mis Enlaces
              </h2>
            </div>
            
            {/* Verificar si hay tabs */}
            {usuario.tabs && usuario.tabs.length > 0 ? (
              // Layout horizontal (tabs tradicionales)
              <div className='mx-2'>
                <Tabs 
                  selectedKey={tabSeleccionado}
                  onSelectionChange={setTabSeleccionado}
                  variant="underlined"
                  className="w-full"
                  size={estiloMiniatura ? "sm" : "md"}
                  classNames={{
                    tabList: `gap-${estiloMiniatura ? '2' : '4'} w-full relative rounded-none p-0 border-b border-divider`,
                    cursor: "w-full bg-primary",
                    tab: "max-w-fit px-0 h-10 sm:h-12",
                    tabContent: "group-data-[selected=true]:text-primary"
                  }}
                >
                  {usuario.tabs.map((tab) => {
                    const enlacesTab = usuario.enlaces.filter(enlace => 
                      enlace.tab === tab.id || (!enlace.tab && tab.id === 'general')
                    )
                    
                    if (enlacesTab.length === 0) return null
                    
                    return (
                      <Tab 
                        key={tab.id}
                        title={
                          <div className="flex items-center space-x-2">
                            <span className={estiloMiniatura ? 'text-sm' : ''}>{renderIcon(tab.icono)}</span>
                            <span className={estiloMiniatura ? 'text-sm' : ''}>{tab.nombre}</span>
                            <Chip size="sm" variant="flat" color="default">
                              {enlacesTab.length}
                            </Chip>
                          </div>
                        }
                      >
                        {/* Descripción del tab - Solo visible cuando no es miniatura */}
                        {!estiloMiniatura && tab.descripcion && (
                          <div className="mt-1 mb-2 p-1 rounded-lg ">
                            <div className="flex items-start gap-3">
                              <div className="flex-1">
                                <p className="text-sm text-gray-600 leading-relaxed">
                                  {tab.descripcion}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Renderizado de enlaces del tab */}
                        <div className="space-y-2 sm:space-y-4 mb-10">
                          {enlacesTab.map((enlace, index) => (
                            enlace.esCuentaBancaria ? (
                              <RenderizarCuentaBancaria
                                key={enlace.id || index}
                                enlace={enlace}
                                showEditButtons={false}
                              />
                            ) : (
                              <RenderizadorDiseño
                                key={enlace.id || index}
                                diseño={usuario.configuracionDisenos?.disenoGlobal || 'card'}
                                enlaces={[enlace]}
                                estilosMiniatura={estiloMiniatura}
                                esPreview={false}
                                configuracionDiseño={usuario.configuracionDisenos?.configuraciones?.[usuario.configuracionDisenos?.disenoGlobal] || {}}
                              />
                            )
                          ))}
                        </div>
                      </Tab>
                    )
                  })}
                </Tabs>
              </div>
            ) : (
              // Vista tradicional sin tabs
              <div className={` my-4 flex mx-1`}>
                <div className="space-y-2 sm:space-y-4 mb-10 ">
                  {enlacesFiltrados.map((enlace, index) => (
                    enlace.esCuentaBancaria ? (
                      <RenderizarCuentaBancaria
                        key={enlace.id || index}
                        enlace={enlace}
                        showEditButtons={false}
                      />
                    ) : (
                      <RenderizadorDiseño
                        key={enlace.id || index}
                        diseño={usuario.configuracionDisenos?.disenoGlobal || 'card'}
                        enlaces={[enlace]}
                        estilosMiniatura={estiloMiniatura}
                        esPreview={false}
                        configuracionDiseño={usuario.configuracionDisenos?.configuraciones?.[usuario.configuracionDisenos?.disenoGlobal] || {}}
                      />
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mostrar mensaje cuando no hay enlaces y es el perfil propio */}
      {esPerfilPropio && !estiloMiniatura && (!usuario.enlaces || usuario.enlaces.length === 0) && (
        <Card 
          className="mb-4"
          style={{
            backgroundColor: estilosPerfil.fondoSecundario,
            borderColor: estilosPerfil.borde
          }}
        >
          <CardBody 
            className="text-center py-8"
            style={{ color: estilosPerfil.texto }}
          >
            <div className="mb-4">
              <i className="fas fa-link text-4xl text-gray-400 mb-4"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              ¡Añade tu primer enlace!
            </h3>
            <p className="text-gray-500 mb-4">
              Comparte tus redes sociales, sitio web, tienda online y más.
            </p>
            <Button
              onClick={abrirModalNuevoEnlace}
              startContent={<i className="fas fa-plus"></i>}
              style={{
                backgroundColor: estilosPerfil.primario,
                color: 'white'
              }}
              className="font-semibold"
            >
              Crear Primer Enlace
            </Button>
          </CardBody>
        </Card>
      )}

      {/* Footer */}
      {!estiloMiniatura && (
        <div className="text-center mt-8 py-4">
          <p 
            className="text-sm"
            style={{ color: estilosPerfil.textoSecundario }}
          >
            Perfil público de {usuario.nombre} • Creado con Wachalo.co
          </p>
        </div>
      )}
    </div>
  )
}

export default VistaPerfilPublico 